import { EventLifecycleButtons, EventStatusBadge, SaveAsTemplateDialog } from '@/components/events';
import { VisibilityBadge } from '@/components/shared';
import type { Event } from '@/config/objects/events';
import {
  Button,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EntityDetailShell,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Camera,
  Download,
  LayoutTemplate,
  Monitor,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { buildDisplayUrl } from '@/lib/displayToken';
import { getEventTabs } from '@/pages/events/eventTabs';

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<Event | null>(null);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
  const basePath = `/events/${id}`;

  // Same query the shell runs (deduped by TanStack) — we need the status
  // up front to order the tabs: Live leads while running, review surfaces
  // lead once wrapped, structure leads while building.
  const { data: eventData } = useQuery<{ success: boolean; data: Event }>({
    queryKey: ['Event', id],
    queryFn: () => apiClient.get(`/events/${id}`),
    enabled: !!id,
  });
  const tabs = getEventTabs(eventData?.data?.status);

  const deleteMutation = useMutation({
    mutationFn: async (
      eventId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/events/${eventId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Event'] });
        navigate('/events');
      } else {
        throw new Error(response.error || 'Failed to delete event');
      }
    },
  });

  const handleDelete = () => {
    if (!pendingDelete) return;
    toast.promise(deleteMutation.mutateAsync(pendingDelete.id), {
      loading: 'Deleting event...',
      success: 'Event deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete event',
    });
  };

  /**
   * Trigger a JSON download of the export envelope. We fetch via
   * apiClient (not a raw <a download>) so the auth token rides on
   * the request — the export endpoint is admin-only.
   */
  const handleExport = async (event: Event) => {
    try {
      const envelope = await apiClient.get<unknown>(`/events/${event.id}/export`);
      const blob = new Blob([JSON.stringify(envelope, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (event.name || 'event').replace(/[^a-zA-Z0-9-_]+/g, '_').slice(0, 60);
      link.download = `${safeName}-export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Event exported');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to export event';
      toast.error(message);
    }
  };

  /**
   * Mint a long-lived, event-scoped display token and hand back a link
   * that works on any screen with no sign-in (TV, projector, lobby
   * kiosk). Copies the link to the clipboard and opens it in a new tab.
   */
  const handleDisplayScreen = async (event: Event, mode?: string) => {
    try {
      const response = await apiClient.get<{ success: boolean; data: { token: string } }>(
        `/events/${event.id}/display-token`,
      );
      const token = response?.data?.token;
      if (!token) {
        throw new Error('The server did not return a display token');
      }
      const url = buildDisplayUrl(window.location.origin, event.id, token, mode);
      // Clipboard access can be denied (permissions, non-secure context);
      // still open the display so the action isn't a dead end.
      let copied = true;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        copied = false;
      }
      window.open(url, '_blank');
      toast.success(
        copied
          ? 'Display link copied — safe to open on any screen'
          : 'Display opened — copying the link to the clipboard failed',
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to create the display link';
      toast.error(message);
    }
  };

  return (
    <>
      <EntityDetailShell<Event>
        resource="events"
        queryKey="Event"
        id={id}
        basePath={basePath}
        entityLabel="Event"
        tabs={tabs}
        contextKey="event"
        hero={{
          category: { label: 'Event', icon: Calendar },
          title: (e) => e?.name || 'Event',
          fallbackIcon: Calendar,
          badges: (e) => (
            <>
              {e.status && <EventStatusBadge status={e.status} variant="solid" />}
              {e.visibility && <VisibilityBadge visibility={e.visibility} variant="solid" />}
              {e.startTime && (
                <span className="text-sm text-white/70">
                  {new Date(e.startTime).toLocaleDateString()}
                </span>
              )}
            </>
          ),
        }}
        breadcrumbs={(e) => [
          { label: 'Home', href: '/' },
          { label: 'Events', href: '/events' },
          { label: e?.name || 'Event', current: true },
        ]}
        headerActions={(e) =>
          e ? (
            <>
              {/* Primary: the status-relevant lifecycle action(s). */}
              <EventLifecycleButtons event={e} className="contents" />
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              {/* Secondary actions tucked into an overflow menu so the
                  header stays focused on the lifecycle + edit actions. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" aria-label="More actions">
                    <MoreHorizontal className="h-4 w-4" />
                    More
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* One display action: mints an event-scoped token, copies
                      the shareable link (works signed-out on any TV/screen),
                      and opens it — no separate "for me" vs "for the venue"
                      items to choose between. */}
                  <DropdownMenuItem onClick={() => handleDisplayScreen(e)}>
                    <Monitor className="h-4 w-4" />
                    Display View
                  </DropdownMenuItem>
                  {/* DT-166. Same tokenized link, deep-linked into slideshow
                      mode — saves a click during the post-event presentation. */}
                  <DropdownMenuItem onClick={() => handleDisplayScreen(e, 'slideshow')}>
                    <Camera className="h-4 w-4" />
                    Slideshow
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport(e)}>
                    <Download className="h-4 w-4" />
                    Export
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowSaveAsTemplate(true)}>
                    <LayoutTemplate className="h-4 w-4" />
                    Save as Template
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={() => setPendingDelete(e)}>
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : null
        }
      />

      {showSaveAsTemplate && eventData?.data && (
        <SaveAsTemplateDialog
          event={eventData.data}
          open={showSaveAsTemplate}
          onOpenChange={setShowSaveAsTemplate}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Delete Event"
        description={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
